import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import "../styles/PaymentSandF.css";
import { SKILLMASTER_API } from "./config";
import { PENDING_PAYMENT_KEY } from "./Checkout";

const PaymentSuccess = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const txn = new URLSearchParams(location.search).get("txn");
  const [showConfetti, setShowConfetti] = useState(true);
  const [payment, setPayment] = useState(null);
  const [checking, setChecking] = useState(true);
  const [book, setBook] = useState(location.state?.book || null);

  // PayGlocal redirects here; confirm with our server before unlocking the book
  useEffect(() => {
    if (!txn) {
      navigate("/payment-failed", { replace: true });
      return;
    }
    let pending = null;
    try {
      pending = JSON.parse(localStorage.getItem(PENDING_PAYMENT_KEY) || "null");
    } catch (e) {
      pending = null;
    }
    fetch(`${SKILLMASTER_API}/payment/status/${encodeURIComponent(txn)}`)
      .then((r) => r.json())
      .then((data) => {
        const p = data?.payment;
        if (!data?.success || !p?.paid) {
          navigate(`/payment-failed?txn=${encodeURIComponent(txn)}&status=${encodeURIComponent(p?.status || "")}`, { replace: true });
          return;
        }
        setPayment(p);
        const paidBook = pending?.txn === txn && pending.book
          ? pending.book
          : { id: p.book_id, source: p.book_source, pdf_name: p.book_name, price: p.amount };
        setBook(paidBook);
        try {
          const purchased = JSON.parse(localStorage.getItem("purchasedBooks") || "[]");
          if (!purchased.some((item) => item.pdf_name === paidBook.pdf_name)) {
            purchased.push(paidBook);
            localStorage.setItem("purchasedBooks", JSON.stringify(purchased));
          }
          localStorage.removeItem(PENDING_PAYMENT_KEY);
        } catch (e) {
          /* storage unavailable */
        }
      })
      .catch(() => navigate(`/payment-failed?txn=${encodeURIComponent(txn)}`, { replace: true }))
      .finally(() => setChecking(false));

    // Hide confetti after 3 seconds
    const timer = setTimeout(() => {
      setShowConfetti(false);
    }, 3000);

    return () => clearTimeout(timer);
  }, [txn, navigate]);

  const handleDownloadPDF = () => {
    if (book?.pdf_link) {
      window.open(book.pdf_link, '_blank');
    }
  };

  const formatTransactionId = (id) => {
    if (!id) return "N/A";
    return id.length > 12 ? `${id.substring(0, 12)}...` : id;
  };

  if (checking || !payment) {
    return (
      <div className="payment-result-container success">
        <div className="result-card">
          <div className="result-content">
            <h1>Confirming your payment...</h1>
            <p className="success-message">Please wait, do not close this page.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="payment-result-container success">
      {showConfetti && <div className="confetti"></div>}
      
      <div className="result-card">
        {/* Success Animation */}
        <div className="success-animation">
          <div className="checkmark-circle">
            <svg className="checkmark" width="40" height="40" viewBox="0 0 24 24" fill="none">
              <path 
                d="M20 6L9 17L4 12" 
                stroke="white" 
                strokeWidth="3" 
                strokeLinecap="round" 
                strokeLinejoin="round"
              />
            </svg>
          </div>
        </div>

        {/* Success Content */}
        <div className="result-content">
          <h1>Payment Successful</h1>
          <p className="success-message">
            Your purchase has been completed successfully. You now have access to your book!
          </p>

          {/* Transaction Details */}
          <div className="transaction-details">
            <div className="detail-row">
              <span className="label">Book</span>
              <span className="value">{book?.pdf_name || "Unknown Book"}</span>
            </div>
            <div className="detail-row">
              <span className="label">Amount Paid</span>
              <span className="value amount">₹{payment.amount}</span>
            </div>
            <div className="detail-row">
              <span className="label">Transaction ID</span>
              <span className="value transaction-id">
                {formatTransactionId(payment.gid || payment.merchant_txn_id)}
              </span>
            </div>
            <div className="detail-row">
              <span className="label">Status</span>
              <span className="value success-status">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path 
                    d="M20 6L9 17L4 12" 
                    stroke="#10b981" 
                    strokeWidth="2" 
                    strokeLinecap="round" 
                    strokeLinejoin="round"
                  />
                </svg>
                Completed
              </span>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="success-actions">
            {book?.pdf_link && (
            <button className="btn-primary download-btn" onClick={handleDownloadPDF}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 19.5A2.5 2.5 0 016.5 17H20" />
                <path d="M4 4.5A2.5 2.5 0 016.5 7H20V20H6.5A2.5 2.5 0 014 17.5V4.5Z" />
                <line x1="8" y1="12" x2="16" y2="12" />
                <line x1="8" y1="16" x2="16" y2="16" />
              </svg>
              Read Book
            </button>
            )}
            <button className="btn-secondary library-btn" onClick={() => navigate("/allpages")}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path 
                  d="M4 19.5A2.5 2.5 0 016.5 17H20" 
                  stroke="currentColor" 
                  strokeWidth="2" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                />
                <path 
                  d="M6.5 2H20V22H6.5A2.5 2.5 0 014 19.5V4.5A2.5 2.5 0 016.5 2Z" 
                  stroke="currentColor" 
                  strokeWidth="2" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                />
              </svg>
              Go to Library
            </button>
          </div>

          {/* Receipt Note */}
          <div className="receipt-note">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path 
                d="M14 2H6A2 2 0 004 4V20A2 2 0 006 22H18A2 2 0 0020 20V8L14 2Z" 
                stroke="currentColor" 
                strokeWidth="2" 
                strokeLinecap="round" 
                strokeLinejoin="round"
              />
              <polyline points="14,2 14,8 20,8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Payment confirmed by PayGlocal for {payment.email}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccess;